import React, { useCallback } from 'react';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { CHART_TYPE, DISPLAY_VALUES, LABEL_FONT_COLOR, LABEL_FONT_FAMILY, LABEL_FONT_SIZE } from 'constants/charts';
import { localizeAreaLabel } from 'utils/helpers/units';

import BarChart from './BarChart';
import ColumnChart from './ColumnChart';
import LineChart from './LineChart';
import { DataItemType } from './types';
import {
  communityNameAndContractText,
  formatYAxisValue,
  labelFormatter,
  normalizeLineChartData,
  textStyle,
  tooltipContent,
  tooltipFormatter,
} from './utils';
import { formatValueDifference, shouldDisplayValueDifference } from '../valueDifferenceUtils';
import { DATA_RANGE_TYPE } from '../Widget/constants';
import { useWidgetContext } from '../Widget/WidgetContext';

interface Props {
  widgetType: number;
  type: string;
  data: DataItemType[];
  xField: string;
  yField: string;
  seriesField: string;
  valueDisplay?: number;
  comparisonTypeEnabled?: boolean;
}

export const Chart = ({
  widgetType,
  type,
  data,
  xField,
  yField,
  seriesField,
  valueDisplay,
  comparisonTypeEnabled,
}: Props) => {
  const { community } = useCommunityViewContext();
  const { displayValue, rangeType } = useWidgetContext();
  const showValueDifference = shouldDisplayValueDifference(comparisonTypeEnabled, valueDisplay);

  const colorCallback = useCallback(
    (colorData: any) => {
      const dataEntry = data.find((item: any) => item[seriesField] === colorData[seriesField]);
      let gradient = dataEntry?.community_gradient;
      if (type === CHART_TYPE.COLUMN && gradient) {
        // to apply gradient vertically in case of Column Chart
        gradient = gradient.replace(/^l\(\d+\)/, 'l(270)');
        return gradient;
      }
      return gradient ?? '';
    },
    [data, type, seriesField]
  );

  const borderCallback = useCallback(
    (colorData: any) => {
      const dataEntry = data.find((item: any) => item[seriesField] === colorData[seriesField]);
      const color = dataEntry?.community_color ?? '';
      return {
        stroke: color,
        lineWidth: 1,
      };
    },
    [data, type, seriesField]
  );

  const normalizedData = type === CHART_TYPE.LINE ? normalizeLineChartData(widgetType, data) : data;

  const showLabel = displayValue === DISPLAY_VALUES.DISPLAY.value;
  const groupedChart = rangeType === DATA_RANGE_TYPE.GROUPED;
  const isValueXAxis = type === CHART_TYPE.BAR;

  const formatLabelValue = (item: any) => {
    const baseLabel = labelFormatter(item?.value, widgetType, community);
    if (!showValueDifference) {
      return baseLabel;
    }

    const differenceLabel = formatValueDifference(item?.difference, widgetType, valueDisplay, community);
    if (!differenceLabel) {
      return baseLabel;
    }

    return `${baseLabel} ${differenceLabel}`.trim();
  };

  const config = {
    data: normalizedData,
    autoFit: true,
    xField: type === CHART_TYPE.LINE ? 'quarterDate' : xField,
    yField,
    seriesField,
    renderer: 'svg' as const,
    animation: false,
    appendPadding: [10, 45, 20, 0],
    theme: {
      styleSheet: {
        backgroundColor: 'white' as const,
      },
    },
    legend: false as const,
    xAxis: {
      animate: undefined,
      label: {
        formatter: (title: string) =>
          isValueXAxis ? formatYAxisValue(title, widgetType, community) : localizeAreaLabel(title, community),
        style: {
          fill: LABEL_FONT_COLOR,
          fontFamily: LABEL_FONT_FAMILY,
          fontSize: LABEL_FONT_SIZE,
        },
      },
      tickLine: null,
    },
    yAxis: {
      animate: undefined,
      label: {
        style: (label: string) => textStyle(label, data),
        autoEllipsis: true,
        formatter: (title: string) => communityNameAndContractText(title, data),
      },
      tickLine: null,
    },
    colorField: 'type',
    color: colorCallback,
    barStyle: borderCallback,
    columnStyle: borderCallback,
    label: showLabel
      ? {
          position: type === CHART_TYPE.BAR ? ('right' as const) : ('top' as const),
          style: {
            fontSize: LABEL_FONT_SIZE,
          },
          rotate: groupedChart ? -Math.PI / 2 : 0,
          offsetX: groupedChart ? (showValueDifference ? 25 : 10) : 0,
          offsetY: groupedChart ? 5 : 0,
          autoHide: false as boolean,
          autoRotate: true as boolean,
          formatter: (datum: any) => formatLabelValue(datum),
        }
      : undefined,
    tooltip: {
      shared: false,
      customContent: (title: string, tooltipItems: any) =>
        tooltipContent(widgetType, tooltipItems, type, valueDisplay, community),
      formatter: (item: any) => tooltipFormatter(item, widgetType, valueDisplay, community),
      domStyles: {
        'g2-tooltip': {
          opacity: 1,
          borderRadius: 0,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          padding: 0,
        },
      },
    },
  };

  return (
    <>
      {type === CHART_TYPE.BAR && <BarChart {...config} />}
      {type === CHART_TYPE.COLUMN && <ColumnChart widgetType={widgetType} columnProps={config} />}
      {type === CHART_TYPE.LINE && <LineChart widgetType={widgetType} lineProps={config} />}
    </>
  );
};

export default Chart;
